import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { InboxArrowDownIcon } from '@heroicons/react/24/outline';
import { driveClient } from '@/lib/drive/driveClient';
import type { FileRequestRow } from '@/lib/drive/driveClient';
import { fileRequestUrl } from '@/lib/office/officeApps';
import { formatTimestamp } from '@/lib/drive/format';
import { appPrompt } from '@/lib/dialogs';

/**
 * File requests: a public link that collects uploads into one of the owner's folders —
 * the receiver needs no account. Create, copy the link, watch the submission counter,
 * delete when done.
 */
export function DriveRequestsView() {
  const { t, i18n } = useTranslation();
  const [rows, setRows] = useState<FileRequestRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setRows(await driveClient.listFileRequests());
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setRows([]);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const create = async () => {
    const title = await appPrompt(t('drive.requestsTitlePrompt'), t('drive.requestsDefaultTitle'), {
      title: t('drive.newRequest'),
      confirmLabel: t('common.save'),
      cancelLabel: t('common.cancel'),
    });
    if (title === null) return;
    try {
      await driveClient.createFileRequest(title.trim() || t('drive.requestsDefaultTitle'), null, 30);
      toast.success(t('drive.requestCreated'));
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : String(err));
    }
  };

  const copyLink = async (row: FileRequestRow) => {
    await navigator.clipboard.writeText(fileRequestUrl(row.shortId)).catch(() => undefined);
    toast.success(t('drive.linkCopied', { url: '' }));
  };

  const remove = async (row: FileRequestRow) => {
    try {
      await driveClient.deleteFileRequest(row.id);
      toast.success(t('drive.requestDeleted'));
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : String(err));
    }
  };

  return (
    <div className="flex min-h-48 flex-1 flex-col overflow-hidden rounded-lg border border-border-color bg-card">
      <div className="flex flex-nowrap items-center justify-between gap-3 border-b border-border-color px-4 py-2.5">
        <div className="flex min-w-0 items-center gap-2">
          <InboxArrowDownIcon className="h-4 w-4 flex-shrink-0 text-accent" />
          <h2 className="min-w-0 truncate whitespace-nowrap text-sm font-medium text-foreground">{t('drive.fileRequests')}</h2>
        </div>
        <button
          type="button"
          onClick={() => void create()}
          className="flex-shrink-0 whitespace-nowrap rounded-md bg-accent px-3 py-1.5 text-sm font-medium text-background transition-opacity hover:opacity-90"
        >
          {t('drive.newRequest')}
        </button>
      </div>
      {rows === null ? (
        <div className="flex flex-1 items-center justify-center p-8">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-border-color border-t-accent" />
        </div>
      ) : rows.length === 0 ? (
        <div className="flex flex-1 items-center justify-center p-8">
          <p className="text-sm text-muted-foreground">{error ?? t('drive.requestsEmpty')}</p>
        </div>
      ) : (
        <ul className="divide-y divide-border-color overflow-y-auto">
          {rows.map((row) => (
            <li key={row.id} className="flex flex-nowrap items-center gap-3 px-4 py-2.5">
              <div className="min-w-0 flex-1">
                <p className="truncate whitespace-nowrap text-sm text-foreground">{row.title}</p>
                <p className="whitespace-nowrap text-xs text-muted-foreground">
                  {row.folderName ?? '/'} · {row.submissions} {t('drive.requestSubmissions')}
                  {row.expiresAt ? ` · ${formatTimestamp(new Date(row.expiresAt).getTime(), i18n.resolvedLanguage)}` : ''}
                  {row.active ? '' : ` · ${t('drive.requestClosed')}`}
                </p>
              </div>
              <button
                type="button"
                onClick={() => void copyLink(row)}
                className="flex-shrink-0 whitespace-nowrap rounded-md px-2 py-1 text-xs text-foreground transition-colors hover:bg-muted"
              >
                {t('drive.copy')}
              </button>
              <button
                type="button"
                onClick={() => void remove(row)}
                className="flex-shrink-0 whitespace-nowrap rounded-md px-2 py-1 text-xs text-destructive transition-colors hover:bg-muted"
              >
                {t('drive.delete')}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
